import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Auth0Provider, useAuth0, type AppState, type User } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import { config } from '../config';
import { clearRumIdentity, setRumIdentity } from '../observability/rum';
import { safeNextPath } from '../router/safeNextPath';

/** Namespaced custom claims minted by the deployed `add-tenant-claim` action. */
const CLAIM_TENANT_SLUG = 'https://agentisai/tenantSlug';
const CLAIM_TENANT_ID = 'https://agentisai/tenantId';

/** App-derived brand per tenant slug — NOT an Auth0 claim. */
interface TenantBrand {
  displayName: string;
  primaryColor: string;
  avatarTint: string;
}
const TENANT_BRAND: Record<string, TenantBrand> = {
  bgprivate: { displayName: 'BG Private', primaryColor: '#1e40af', avatarTint: '#1e40af' },
};

export interface CurrentUser {
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
  user?: User;
  tenantSlug?: string;
  tenantId?: string;
  tenantBrand?: TenantBrand;
  /** E3: pass the post-login return path (validated relative path or '/'). */
  login: (returnTo?: string) => void;
  logout: () => void;
  /** E3 seam — the API client (not E2) calls this to attach a Bearer token. */
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<CurrentUser | null>(null);

/**
 * OAuth error codes from `getAccessTokenSilently()` that mean **interactive
 * re-auth is required** — the cached/refresh token can't be renewed silently
 * (expired refresh token, dead Auth0 session, iframe silent-auth blocked by
 * third-party-cookie policy). On these we re-initiate login (R13: auth failure
 * → re-auth) instead of dead-ending the caller on a generic error. We do NOT
 * redirect on transient errors (network/timeout) — only on these terminal
 * auth-state codes — so a blip can't bounce the user to Auth0.
 */
const REAUTH_ERROR_CODES = new Set([
  'login_required',
  'consent_required',
  'mfa_required',
  'missing_refresh_token',
  'invalid_grant',
]);

function needsReauth(err: unknown): boolean {
  const code = (err as { error?: unknown } | null)?.error;
  return typeof code === 'string' && REAUTH_ERROR_CODES.has(code);
}

/**
 * Inner provider — runs INSIDE <Auth0Provider> so it can read useAuth0().
 * Projects the JWT custom claims + the app-derived tenant brand onto the
 * typed shape the rest of the app consumes via useAuth().
 */
function InnerAuthProvider({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    error,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const tenantSlug = user?.[CLAIM_TENANT_SLUG] as string | undefined;
  const tenantId = user?.[CLAIM_TENANT_ID] as string | undefined;
  const tenantBrand = tenantSlug ? TENANT_BRAND[tenantSlug] : undefined;

  // RUM identity enrichment (S17a / ADR-0027). The accepted-PII decision —
  // tenant + email (the Auth0 email, per ADR-0027 over the opaque `sub`) —
  // are stamped onto the RUM session AND cached so every error event
  // payload carries them. No-op until RUM is initialised (local dev).
  // Credentials are NEVER attached — only identity.
  const email = typeof user?.email === 'string' ? user.email : undefined;
  useEffect(() => {
    if (isAuthenticated && tenantSlug && email) {
      setRumIdentity({ tenant: tenantSlug, username: email });
    } else if (!isAuthenticated) {
      clearRumIdentity();
    }
  }, [isAuthenticated, tenantSlug, email]);

  const value: CurrentUser = {
    isAuthenticated,
    isLoading,
    error,
    user,
    tenantSlug,
    tenantId,
    tenantBrand,
    login: (returnTo?: string) =>
      void loginWithRedirect({ appState: { returnTo: safeNextPath(returnTo) } }),
    logout: () => void logout({ logoutParams: { returnTo: window.location.origin } }),
    // Self-healing token accessor (the API client calls this before each
    // request). If silent renewal can't recover the session — e.g. the refresh
    // token expired, or no refresh token exists and the iframe fallback is
    // blocked by Safari ITP / third-party-cookie policy — re-initiate login so
    // the user lands back here authenticated, instead of stranding them on a
    // generic "Something went wrong" (the symptom before this: works → idle
    // past the 30-min access token → stuck until manual logout/login). The
    // redirect supersedes the in-flight call; we still reject so the caller
    // unwinds. Only terminal auth-state codes trigger it (not network blips).
    getAccessToken: async () => {
      try {
        return await getAccessTokenSilently();
      } catch (err) {
        if (needsReauth(err)) {
          const returnTo = safeNextPath(window.location.pathname + window.location.search);
          void loginWithRedirect({ appState: { returnTo } });
        }
        throw err;
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
InnerAuthProvider.displayName = 'InnerAuthProvider';

export function AuthProvider({ children }: { children: ReactNode }) {
  // useNavigate() requires a Router ancestor — main.tsx nests <BrowserRouter>
  // OUTSIDE this provider precisely so onRedirectCallback can drive the
  // router instead of bare history.replaceState (the Auth0↔Router crux).
  const navigate = useNavigate();

  // After the /callback code-exchange, land on the validated returnTo.
  // safeNextPath guards the (attacker-influenceable) appState.returnTo.
  const onRedirectCallback = (appState?: AppState) => {
    // navigate() returns a Promise in RR7; we fire-and-forget the redirect.
    void navigate(safeNextPath(appState?.returnTo), { replace: true });
  };

  return (
    <Auth0Provider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        organization: config.auth0.organization,
        audience: config.auth0.audience,
        scope: 'openid profile email',
      }}
    >
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </Auth0Provider>
  );
}
AuthProvider.displayName = 'AuthProvider';

export function useAuth(): CurrentUser {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>.');
  }
  return ctx;
}
